import { useEffect, useState, useCallback, useRef } from "react";
import { Calendar, Search, X, Plus, Pencil, Trash2, Save, Car, ArrowLeft, ChevronUp, ChevronDown, List, CalendarClock, Ban, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import clienteApi from "@/lib/clienteApi";
import { validarCPF } from "@/lib/cpf";
import { useNavigate } from "react-router-dom";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";

const formatarDataHora = (iso) => {
  if (!iso) return "";
  try {
    const [date, timePart] = String(iso).split("T");
    const [y, mo, dd] = (date || "").split("-");
    const hm = (timePart || "").slice(0, 5);
    return `${dd}/${mo}/${y} ${hm}`.trim();
  } catch {
    return iso;
  }
};

// Agendas de hoje: dia/mês + hora, sem o ano (economiza espaço p/ mostrar
// início e fim empilhados). Ex.: "23/09 12:17".
const formatarDDMMHM = (iso) => {
  if (!iso) return "";
  try {
    const [date, timePart] = String(iso).split("T");
    const [, mo, dd] = (date || "").split("-");
    const hm = (timePart || "").slice(0, 5);
    return `${dd}/${mo} ${hm}`.trim();
  } catch {
    return iso;
  }
};

const formatCPFInput = (v) => {
  const n = String(v || "").replace(/\D/g, "").slice(0, 11);
  if (n.length <= 3) return n;
  if (n.length <= 6) return `${n.slice(0, 3)}.${n.slice(3)}`;
  if (n.length <= 9) return `${n.slice(0, 3)}.${n.slice(3, 6)}.${n.slice(6)}`;
  return `${n.slice(0, 3)}.${n.slice(3, 6)}.${n.slice(6, 9)}-${n.slice(9)}`;
};

const formatCPFDisplay = (cpf) => {
  const n = String(cpf || "").replace(/\D/g, "").slice(0, 11);
  if (n.length !== 11) return cpf || "";
  return `${n.slice(0, 3)}.${n.slice(3, 6)}.${n.slice(6, 9)}-${n.slice(9)}`;
};

const parseIsoParts = (iso) => {
  if (!iso) return { date: "", time: "" };
  const [d, t] = String(iso).split("T");
  return { date: d || "", time: (t || "").slice(0, 5) };
};

import { todayLocal } from "@/lib/dateLocal";

// v4.7 — usa `todayLocal()` (helper) em vez de `toISOString().split("T")[0]`
// para evitar bug de UTC (data volta com um dia à frente à noite no fuso -3).
const todayStr = () => todayLocal();

// Sub-componente: form de agenda (criar/editar)
function AgendaForm({ initial, onCancel, onSaved, reagendando = false }) {
  const isEdit = !!initial?.id;
  const [motivos, setMotivos] = useState([]);
  const [loadingDeps, setLoadingDeps] = useState(true);
  const [saving, setSaving] = useState(false);
  // Offset UTC do tenant (fuso_horario, ex.: -3). Usado para calcular "agora"
  // no fuso do cliente (mesma regra do backend), independente do fuso do PC.
  const [fusoH, setFusoH] = useState(null);

  // Filtros do autocomplete "Quem será visitado"
  // Ao editar uma agenda existente, `initial.unidade` guarda a EQL selecionada;
  // usamos como valor inicial do filtro para que o operador visualize a EQL
  // atual e possa alterá-la ou manter.
  const [eqlFiltro, setEqlFiltro] = useState(initial?.unidade || initial?.eql_filtro || "");
  const [nomeBusca, setNomeBusca] = useState(initial?.pessoa_visitada_nome || "");
  const [sugestoes, setSugestoes] = useState([]);
  const [loadingSug, setLoadingSug] = useState(false);
  const [showSug, setShowSug] = useState(false);
  const [form, setForm] = useState(() => {
    const pi = parseIsoParts(initial?.permanencia_inicial);
    const pf = parseIsoParts(initial?.permanencia_final);
    // Para nova agenda: data de hoje + hora ATUAL como default
    const now = new Date();
    const horaAgora = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
    return {
      cpf: formatCPFDisplay(initial?.cpf || ""),
      nome: initial?.nome || "",
      ddi_celular: initial?.ddi_celular || "+55",
      celular: initial?.celular || "",
      motivo: initial?.motivo || "",
      data_inicial: pi.date || todayStr(),
      hora_inicial: pi.time || horaAgora,
      data_final: pf.date || todayStr(),
      hora_final: pf.time || "23:59",
      pessoa_visitada_id: initial?.pessoa_visitada_id || "",
      pessoa_visitada_nome: initial?.pessoa_visitada_nome || "",
      placa: initial?.placa || "",
      modelo_veiculo: initial?.modelo_veiculo || "",
      cor_veiculo: initial?.cor_veiculo || "",
    };
  });

  // Default de início no mount (p/ só auto-corrigir para o fuso do tenant
  // enquanto o operador não editar manualmente os campos de início).
  const mountDefaultRef = useRef(null);
  if (mountDefaultRef.current === null) {
    mountDefaultRef.current = { d: form.data_inicial, h: form.hora_inicial, df: form.data_final };
  }
  // Marca se o operador editou manualmente o INÍCIO (data/hora). Se não editar,
  // o início é carimbado com "agora" no salvamento (feature 1).
  const startTouchedRef = useRef(false);

  useEffect(() => {
    (async () => {
      setLoadingDeps(true);
      try {
        const resMot = await clienteApi.get("/cliente/motivos-visita");
        setMotivos(resMot.data || []);
      } catch {
        toast.error("Erro ao carregar dados auxiliares");
      } finally {
        setLoadingDeps(false);
      }
      // Fuso do tenant (para validar "passado" e sugerir início corretos).
      try {
        const cfg = await clienteApi.get("/cliente/configuracoes");
        const parsed = parseInt(cfg.data?.fuso_horario, 10);
        setFusoH(Number.isFinite(parsed) ? parsed : -3);
      } catch {
        setFusoH(-3);
      }
    })();
  }, []);

  // Ao conhecer o fuso do tenant, ajusta o início padrão (nova agenda OU
  // reagendamento) para "agora" no fuso do cliente — desde que não editado.
  useEffect(() => {
    if ((isEdit && !reagendando) || fusoH == null) return;
    const tn = new Date(Date.now() + fusoH * 3600 * 1000);
    const pad = (n) => String(n).padStart(2, "0");
    const d = `${tn.getUTCFullYear()}-${pad(tn.getUTCMonth() + 1)}-${pad(tn.getUTCDate())}`;
    const h = `${pad(tn.getUTCHours())}:${pad(tn.getUTCMinutes())}`;
    setForm((p) => {
      if (p.data_inicial !== mountDefaultRef.current.d || p.hora_inicial !== mountDefaultRef.current.h) return p;
      const next = { ...p, data_inicial: d, hora_inicial: h };
      if (p.data_final === mountDefaultRef.current.df) next.data_final = d;
      return next;
    });
  }, [fusoH, isEdit, reagendando]);

  // Busca autorizadores conforme o usuário digita (debounce 350ms).
  // Backend retorna [] se nem EQL nem nome forem informados (evita dump de 10k).
  useEffect(() => {
    const eqlClean = (eqlFiltro || "").trim();
    const nomeClean = (nomeBusca || "").trim();
    // Sem filtros ou nome muito curto + sem EQL → não busca
    if (!eqlClean && nomeClean.length < 2) {
      setSugestoes([]);
      return;
    }
    let cancelled = false;
    setLoadingSug(true);
    const handle = setTimeout(async () => {
      try {
        const params = { limit: 30 };
        if (eqlClean) params.eql = eqlClean;
        if (nomeClean) params.nome = nomeClean;
        const r = await clienteApi.get("/cliente/moradores/autorizadores", { params });
        if (!cancelled) setSugestoes(r.data || []);
      } catch {
        if (!cancelled) setSugestoes([]);
      } finally {
        if (!cancelled) setLoadingSug(false);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [eqlFiltro, nomeBusca]);

  const selecionarAutorizador = (m) => {
    setForm((p) => ({ ...p, pessoa_visitada_id: m.id, pessoa_visitada_nome: m.nome || "" }));
    setNomeBusca(m.nome || "");
    if (m.eql) setEqlFiltro(m.eql);
    setShowSug(false);
  };

  // Modal inline para incluir novo motivo
  const [showAddMotivo, setShowAddMotivo] = useState(false);
  const [novoMotivoDesc, setNovoMotivoDesc] = useState("");
  const [novoMotivoEscopo, setNovoMotivoEscopo] = useState("global");
  const [savingMotivo, setSavingMotivo] = useState(false);

  const handleSalvarNovoMotivo = async () => {
    const desc = (novoMotivoDesc || "").trim();
    if (!desc) { toast.error("Informe a descrição do motivo"); return; }
    setSavingMotivo(true);
    try {
      const payload = { descricao: desc, escopo: novoMotivoEscopo };
      const res = await clienteApi.post("/cliente/motivos-visita", payload);
      const novo = res.data;
      // Atualizar lista e selecionar o novo motivo
      setMotivos((prev) => [...prev, novo].sort((a, b) => (a.descricao || "").localeCompare(b.descricao || "")));
      setForm((p) => ({ ...p, motivo: novo.descricao || desc }));
      toast.success("Motivo cadastrado");
      setShowAddMotivo(false);
      setNovoMotivoDesc("");
      setNovoMotivoEscopo("global");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao cadastrar motivo");
    } finally {
      setSavingMotivo(false);
    }
  };

  const salvar = async () => {
    const cpfDigits = (form.cpf || "").replace(/\D/g, "");
    // CPF agora é opcional. Se preenchido, valida; se vazio, permite gravar.
    if (cpfDigits.length > 0) {
      if (cpfDigits.length !== 11) { toast.error("CPF inválido — informe os 11 dígitos ou deixe em branco"); return; }
      if (!validarCPF(cpfDigits)) { toast.error("CPF inválido — verifique os dígitos informados"); return; }
    }
    if (!form.nome.trim()) { toast.error("Informe o nome"); return; }
    if (!form.pessoa_visitada_id) { toast.error("Selecione quem será visitado"); return; }

    // (1) Início não informado/não editado em NOVA agenda → carimba "agora" no
    // fuso do tenant NO MOMENTO DO SALVAMENTO, eliminando o descompasso entre
    // abrir o formulário e salvá-lo.
    let effDataIni = form.data_inicial;
    let effHoraIni = form.hora_inicial;
    if ((!isEdit || reagendando) && !startTouchedRef.current && fusoH != null) {
      const tn = new Date(Date.now() + fusoH * 3600 * 1000);
      const pad = (n) => String(n).padStart(2, "0");
      effDataIni = `${tn.getUTCFullYear()}-${pad(tn.getUTCMonth() + 1)}-${pad(tn.getUTCDate())}`;
      effHoraIni = `${pad(tn.getUTCHours())}:${pad(tn.getUTCMinutes())}`;
    }

    // Janela da agenda: início não pode no passado (apenas em novas agendas),
    // final deve ser maior que o início e a permanência mínima é de 15 minutos.
    const toDate = (d, h) => {
      const [y, mo, da] = String(d || "").split("-").map(Number);
      const [hh, mm] = String(h || "00:00").split(":").map(Number);
      if (!y || !mo || !da) return null;
      return new Date(y, mo - 1, da, hh || 0, mm || 0, 0, 0);
    };
    const dtIni = toDate(effDataIni, effHoraIni);
    const dtFim = toDate(form.data_final, form.hora_final);
    if (!dtIni || !dtFim) { toast.error("Informe data e hora inicial e final"); return; }
    // "Passado" avaliado no FUSO do tenant (mesma regra do backend), com folga
    // de 30 min para o tempo de preenchimento do formulário.
    if ((!isEdit || reagendando) && fusoH != null) {
      const graceMs = 30 * 60 * 1000;
      const nowWallMs = Date.now() + fusoH * 3600 * 1000;
      const [y, mo, da] = effDataIni.split("-").map(Number);
      const [hh, mm] = effHoraIni.split(":").map(Number);
      const startWallMs = Date.UTC(y, mo - 1, da, hh || 0, mm || 0, 0);
      if (startWallMs < nowWallMs - graceMs) {
        toast.error("A data/hora inicial não pode estar no passado. Agende a partir de agora.");
        return;
      }
    }
    if (dtFim.getTime() <= dtIni.getTime()) {
      toast.error("A data/hora final deve ser maior que a inicial.");
      return;
    }
    if (dtFim.getTime() - dtIni.getTime() < 15 * 60 * 1000) {
      toast.error("A permanência mínima é de 15 minutos. Ajuste a data/hora final.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        cpf: cpfDigits, // vazio quando o operador não informou
        nome: form.nome.trim(),
        ddi_celular: form.ddi_celular,
        celular: form.celular.replace(/\D/g, ""),
        motivo: form.motivo,
        data_inicial: effDataIni,
        hora_inicial: effHoraIni,
        data_final: form.data_final,
        hora_final: form.hora_final,
        pessoa_visitada_id: form.pessoa_visitada_id,
        pessoa_visitada_nome: form.pessoa_visitada_nome,
        placa: (form.placa || "").toUpperCase(),
        modelo_veiculo: form.modelo_veiculo,
        cor_veiculo: form.cor_veiculo,
      };
      // Propaga a portaria de origem (sessionStorage do operador logado)
      // para que o log da agenda e o visitante final rastreiem de onde
      // a agenda foi criada.
      try {
        const por = JSON.parse(sessionStorage.getItem("brave_cliente_portaria") || "null");
        if (por && por.id) {
          payload.portaria_id = String(por.id);
          payload.portaria_nome = String(por.nome || "");
          // codigo pode vir como number do backend — força string
          payload.portaria_codigo = por.codigo != null ? String(por.codigo) : "";
        }
      } catch { /* sem portaria */ }
      if (isEdit) {
        await clienteApi.put(`/cliente/agendas/${initial.id}`, payload);
        toast.success("Agenda atualizada");
      } else {
        await clienteApi.post("/cliente/agendas", payload);
        toast.success("Agenda criada");
      }
      onSaved && onSaved();
    } catch (err) {
      // O backend pode retornar detail como string (HTTPException) ou como
      // lista de objetos de validação do Pydantic ({type, loc, msg, input}).
      // Convertemos para string para evitar "Objects are not valid as React child".
      const detail = err?.response?.data?.detail;
      let msg = "Erro ao salvar agenda";
      if (typeof detail === "string") {
        msg = detail;
      } else if (Array.isArray(detail)) {
        msg = detail
          .map((e) => e?.msg || JSON.stringify(e))
          .join(" • ");
      } else if (detail) {
        msg = JSON.stringify(detail);
      }
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const lc = "text-[10px] uppercase tracking-wider font-heading font-bold text-brand-ice/70 mb-1 block";
  const ic = "bg-white/10 border-brand-ice/20 text-brand-ice h-9";

  return (
    <div className="p-4 flex-1 overflow-auto space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <button
          type="button"
          onClick={onCancel}
          className="p-1.5 rounded-lg text-brand-ice/60 hover:text-brand-ice hover:bg-white/10"
          title="Voltar"
          data-testid="agenda-form-voltar"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <h4 className="text-sm font-heading font-bold text-brand-ice">
          {isEdit ? "Editar Agenda" : "Nova Agenda"}
        </h4>
      </div>

      {loadingDeps ? (
        <div className="text-center py-6 text-brand-ice/50">Carregando...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className={lc}>CPF (opcional)</Label>
            <Input
              value={form.cpf}
              onChange={(e) => setForm({ ...form, cpf: formatCPFInput(e.target.value) })}
              onBlur={async () => {
                const cpfDigits = (form.cpf || "").replace(/\D/g, "");
                if (cpfDigits.length !== 11) return;
                if (!validarCPF(cpfDigits)) {
                  toast.error("CPF inválido — verifique os dígitos informados");
                  return;
                }
                if (isEdit) return; // ao editar, não sobrescreve

                // v4.6 — Fallback em cascata:
                // 1) Tenta cliente_visitantes (última visita)
                // 2) Se não achar, tenta cliente_agendas (última agenda) — hospede que
                //    veio como agenda antes e ainda não virou visitante.
                // 3) Se nenhum tiver dado, deixa em branco (novo hóspede).
                const preencher = (v, origem) => {
                  setForm((p) => ({
                    ...p,
                    nome: p.nome || v.nome || "",
                    ddi_celular: v.ddi_celular || p.ddi_celular,
                    celular: p.celular || v.celular || "",
                    motivo: p.motivo || v.motivo || "",
                    placa: p.placa || v.placa || "",
                    modelo_veiculo: p.modelo_veiculo || v.modelo_veiculo || "",
                    cor_veiculo: p.cor_veiculo || v.cor_veiculo || "",
                    pessoa_visitada_id: p.pessoa_visitada_id || v.pessoa_visitada_id || "",
                    pessoa_visitada_nome: p.pessoa_visitada_nome || v.pessoa_visitada_nome || "",
                  }));
                  toast.success(
                    origem === "visitante"
                      ? "Dados da última visita preenchidos"
                      : "Dados da agenda anterior preenchidos"
                  );
                };
                try {
                  const res = await clienteApi.get(`/cliente/visitantes/buscar-cpf/${cpfDigits}`);
                  if (res.data) { preencher(res.data, "visitante"); return; }
                } catch { /* silencia — vai tentar agenda */ }
                try {
                  const res2 = await clienteApi.get(`/cliente/agendas/buscar-cpf/${cpfDigits}`);
                  if (res2.data) { preencher(res2.data, "agenda"); return; }
                } catch { /* silencia — novo hóspede */ }
              }}
              className={ic}
              placeholder="000.000.000-00"
              data-testid="agendaform-cpf"
            />
          </div>
          <div>
            <Label className={lc}>Nome *</Label>
            <Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} className={ic} placeholder="Nome do visitante" data-testid="agendaform-nome" />
          </div>
          <div>
            <Label className={lc}>DDI / Celular</Label>
            <div className="flex gap-2">
              <Input value={form.ddi_celular} onChange={(e) => setForm({ ...form, ddi_celular: e.target.value })} className={`${ic} w-16 text-center`} />
              <Input value={form.celular} onChange={(e) => setForm({ ...form, celular: e.target.value.replace(/\D/g, "").slice(0, 11) })} className={`${ic} flex-1`} placeholder="11999998888" />
            </div>
          </div>
          {/* Linha 2: EQL Autorizador / Quem visitado / Motivo */}
          <div className="md:col-span-3 grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className={lc}>EQL do Autorizador</Label>
              <div className="relative">
                <Input
                  data-testid="agendaform-eql-filtro"
                  value={eqlFiltro}
                  onChange={(e) => {
                    // Força sempre maiúsculas para evitar EQLs duplicadas
                    // (13gm12, 13Gm12 e 13GM12 devem ser equivalentes).
                    setEqlFiltro(e.target.value.toUpperCase());
                    if (form.pessoa_visitada_id) {
                      setForm((p) => ({ ...p, pessoa_visitada_id: "", pessoa_visitada_nome: "" }));
                      setNomeBusca("");
                    }
                  }}
                  onFocus={() => setShowSug(true)}
                  className={`${ic} ${eqlFiltro ? "pr-8" : ""}`}
                  placeholder="Ex: 12-345"
                  style={{ textTransform: "uppercase" }}
                />
                {eqlFiltro && (
                  <button
                    type="button"
                    data-testid="agendaform-eql-clear"
                    onClick={() => {
                      setEqlFiltro("");
                      setForm((p) => ({ ...p, pessoa_visitada_id: "", pessoa_visitada_nome: "" }));
                      setNomeBusca("");
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-brand-ice/50 hover:text-brand-ice hover:bg-white/10"
                    title="Limpar EQL"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
            <div className="relative">
              <Label className={lc}>Quem será visitado *</Label>
              <div className="relative">
                <Input
                  data-testid="agendaform-visitado-busca"
                  value={nomeBusca}
                  onChange={(e) => {
                    setNomeBusca(e.target.value);
                    if (form.pessoa_visitada_id) {
                      setForm((p) => ({ ...p, pessoa_visitada_id: "", pessoa_visitada_nome: "" }));
                    }
                    setShowSug(true);
                  }}
                  onFocus={() => setShowSug(true)}
                  onBlur={() => setTimeout(() => setShowSug(false), 200)}
                  placeholder="Digite parte do nome..."
                  className={`${ic} ${nomeBusca ? "pr-8" : ""}`}
                  autoComplete="off"
                />
                {nomeBusca && (
                  <button
                    type="button"
                    data-testid="agendaform-visitado-clear"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      setNomeBusca("");
                      setForm((p) => ({ ...p, pessoa_visitada_id: "", pessoa_visitada_nome: "" }));
                      setShowSug(false);
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-brand-ice/50 hover:text-brand-ice hover:bg-white/10"
                    title="Limpar"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              {showSug && (sugestoes.length > 0 || loadingSug || (eqlFiltro || nomeBusca.length >= 2)) && (
                <div
                  data-testid="agendaform-visitado-sugestoes"
                  style={{ backgroundColor: "#00002A" }}
                  className="absolute z-50 mt-1 left-0 right-0 border border-brand-ice/30 rounded-lg shadow-2xl max-h-60 overflow-auto"
                >
                  {loadingSug && (
                    <div className="px-3 py-2 text-xs text-brand-ice/60">Buscando autorizadores...</div>
                  )}
                  {!loadingSug && sugestoes.length === 0 && (
                    <div className="px-3 py-2 text-xs text-brand-ice/60">
                      Nenhum autorizador encontrado{eqlFiltro ? ` para a EQL ${eqlFiltro}` : ""}.
                    </div>
                  )}
                  {!loadingSug && sugestoes.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => selecionarAutorizador(m)}
                      className={`w-full text-left px-3 py-2 hover:bg-white/10 transition border-b border-brand-ice/5 last:border-b-0 ${form.pessoa_visitada_id === m.id ? "bg-brand-light/15" : ""}`}
                    >
                      <div className="text-sm text-brand-ice font-medium">{m.nome}</div>
                      <div className="text-[11px] text-brand-ice/60 font-mono">
                        {m.eql ? `EQL ${m.eql}` : "—"}{m.matricula ? ` · Mat. ${m.matricula}` : ""}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label className={lc}>Motivo</Label>
              <div className="flex items-center gap-2">
                {motivos.length > 0 ? (
                  <Select value={form.motivo} onValueChange={(v) => setForm({ ...form, motivo: v })}>
                    <SelectTrigger className={`${ic} flex-1`} data-testid="agendaform-motivo">
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      {motivos.map((m) => (
                        <SelectItem key={m.id} value={m.descricao}>{m.descricao}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input value={form.motivo} onChange={(e) => setForm({ ...form, motivo: e.target.value })} className={`${ic} flex-1`} placeholder="Ex: Reunião..." />
                )}
                <button
                  type="button"
                  onClick={() => setShowAddMotivo(true)}
                  data-testid="agendaform-add-motivo"
                  title="Cadastrar novo motivo"
                  className="flex items-center justify-center w-9 h-9 rounded-lg bg-brand-medium hover:bg-brand-dark text-white shadow-md transition-colors active:scale-[0.97]"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
          {/* Veículo */}
          <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr_auto] gap-3 items-end">
            <div>
              <Label className={`${lc} flex items-center gap-1`}><Car className="w-3 h-3" /> Placa</Label>
              <Input
                value={form.placa}
                onChange={(e) => setForm({ ...form, placa: e.target.value.toUpperCase().slice(0, 8) })}
                onBlur={async () => {
                  const pl = (form.placa || "").trim().toUpperCase();
                  if (pl.length < 3) return;
                  // Se já tem modelo E cor preenchidos, não sobrescreve
                  if ((form.modelo_veiculo || "").trim() && (form.cor_veiculo || "").trim()) return;
                  try {
                    const r = await clienteApi.get("/cliente/veiculos/buscar-por-placa", { params: { placa: pl } });
                    if (r.data?.encontrado) {
                      setForm((p) => ({
                        ...p,
                        modelo_veiculo: p.modelo_veiculo || r.data.modelo || "",
                        cor_veiculo: p.cor_veiculo || r.data.cor || "",
                      }));
                      toast.success(`Placa localizada (${r.data.origem.replace(/_/g, " ")})`);
                    }
                  } catch {
                    // silencioso
                  }
                }}
                className={`${ic} uppercase font-mono`}
                placeholder="ABC1D23"
                data-testid="agendaform-placa"
              />
            </div>
            <div>
              <Label className={lc}>Modelo</Label>
              <Input value={form.modelo_veiculo} onChange={(e) => setForm({ ...form, modelo_veiculo: e.target.value })} className={ic} placeholder="Honda Civic" />
            </div>
            <div>
              <Label className={lc}>Cor</Label>
              <Input value={form.cor_veiculo} onChange={(e) => setForm({ ...form, cor_veiculo: e.target.value })} className={ic} placeholder="Preto" />
            </div>
            <button
              type="button"
              data-testid="agendaform-veiculo-clear"
              onClick={() => setForm((p) => ({ ...p, placa: "", modelo_veiculo: "", cor_veiculo: "" }))}
              disabled={!form.placa && !form.modelo_veiculo && !form.cor_veiculo}
              title="Limpar placa, modelo e cor"
              className="h-10 w-10 flex items-center justify-center rounded-md bg-white/5 hover:bg-white/10 text-brand-ice/70 hover:text-brand-ice border border-brand-ice/15 transition disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          {/* Data/Hora — Inicial e Final na mesma linha (4 colunas) */}
          <div className="md:col-span-3 grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <Label className={lc}>Data Inicial</Label>
              <Input type="date" value={form.data_inicial} onChange={(e) => { startTouchedRef.current = true; setForm({ ...form, data_inicial: e.target.value }); }} className={`${ic} text-xs px-2`} data-testid="agendaform-data-inicial" />
            </div>
            <div>
              <Label className={lc}>Hora Inicial</Label>
              <Input type="time" value={form.hora_inicial} onChange={(e) => { startTouchedRef.current = true; setForm({ ...form, hora_inicial: e.target.value }); }} className={`${ic} text-xs px-2`} data-testid="agendaform-hora-inicial" />
            </div>
            <div>
              <Label className={lc}>Data Final</Label>
              <Input type="date" value={form.data_final} onChange={(e) => setForm({ ...form, data_final: e.target.value })} className={`${ic} text-xs px-2`} />
            </div>
            <div>
              <Label className={lc}>Hora Final</Label>
              <Input type="time" value={form.hora_final} onChange={(e) => setForm({ ...form, hora_final: e.target.value })} className={`${ic} text-xs px-2`} />
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-end gap-2 pt-3">
        <Button variant="outline" onClick={onCancel} className="border-brand-ice/30 text-brand-ice hover:bg-brand-ice/10">
          Cancelar
        </Button>
        <Button onClick={salvar} disabled={saving} data-testid="agendaform-salvar" className="bg-brand-medium hover:bg-brand-dark text-white gap-2">
          <Save className="w-4 h-4" />
          {saving ? "Salvando..." : "Salvar"}
        </Button>
      </div>

      {/* Mini-modal: Cadastrar Novo Motivo (overlay sobre o modal de agenda) */}
      {showAddMotivo && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4 backdrop-blur-sm" onClick={() => !savingMotivo && setShowAddMotivo(false)}>
          <div
            className="relative w-full max-w-md rounded-2xl bg-login-gradient border border-white/15 shadow-[0_0_50px_rgba(80,140,220,0.4)] p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(255,255,255,0.10)_0%,_transparent_70%)] rounded-2xl" />
            <div className="relative z-10 space-y-4">
              <div className="flex items-center gap-2">
                <Plus className="w-5 h-5 text-brand-light" />
                <h4 className="font-heading font-bold text-brand-ice text-base">Novo Motivo de Visita</h4>
              </div>
              <div>
                <Label className={lc}>Descrição *</Label>
                <Input
                  autoFocus
                  value={novoMotivoDesc}
                  onChange={(e) => setNovoMotivoDesc(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleSalvarNovoMotivo(); }}
                  className={ic}
                  placeholder="Ex: Entrega de Encomenda"
                  data-testid="novo-motivo-descricao"
                />
              </div>
              <div>
                <Label className={lc}>Escopo</Label>
                <Select value={novoMotivoEscopo} onValueChange={(v) => setNovoMotivoEscopo(v)}>
                  <SelectTrigger className={ic} data-testid="novo-motivo-escopo">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="global">Global (todos os locais)</SelectItem>
                    <SelectItem value="local">Apenas o local desta agenda</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button
                  variant="outline"
                  onClick={() => { setShowAddMotivo(false); setNovoMotivoDesc(""); }}
                  disabled={savingMotivo}
                  className="border-brand-ice/30 text-brand-ice hover:bg-brand-ice/10"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleSalvarNovoMotivo}
                  disabled={savingMotivo || !novoMotivoDesc.trim()}
                  data-testid="novo-motivo-salvar"
                  className="bg-brand-medium hover:bg-brand-dark text-white gap-2"
                >
                  <Save className="w-4 h-4" />
                  {savingMotivo ? "Salvando..." : "Salvar"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AgendasHojeModal({ open, onClose, onConfirmed, initialView = "list", initialCpf = "", initialVencidas = false }) {
  const navigate = useNavigate();
  const [agendas, setAgendas] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filtro, setFiltro] = useState("");
  // modo: "hoje" (default) | "todos" (hoje + futuras) | "vencidas" (fim já passou)
  const [modo, setModo] = useState("hoje");
  const [vencidasCount, setVencidasCount] = useState(0);
  // Ordenação do relatório — default pela data/hora inicial (asc)
  const [sortKey, setSortKey] = useState("permanencia_inicial");
  const [sortDir, setSortDir] = useState("asc");
  // view: "list" | "form"
  const [view, setView] = useState(initialView);
  const [editing, setEditing] = useState(null);
  const [reagendarMode, setReagendarMode] = useState(false);
  const [cpfPrompt, setCpfPrompt] = useState(null); // { agenda, cpfValor }
  const [savingCpfPrompt, setSavingCpfPrompt] = useState(false);
  // Fuso do tenant (offset UTC) para detectar agendas vencidas (final < agora).
  const [fusoH, setFusoH] = useState(-3);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const cfg = await clienteApi.get("/cliente/configuracoes");
        const parsed = parseInt(cfg.data?.fuso_horario, 10);
        setFusoH(Number.isFinite(parsed) ? parsed : -3);
      } catch { setFusoH(-3); }
      try {
        const vc = await clienteApi.get("/cliente/agendas/count-vencidas");
        setVencidasCount(vc.data?.count || 0);
      } catch { /* ignore */ }
    })();
  }, [open]);

  // Uma agenda está "vencida" quando o fim já passou no fuso do tenant.
  const isVencida = useCallback((a) => {
    const pf = a?.permanencia_final;
    if (!pf) return false;
    const m = String(pf).match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
    if (!m) return false;
    const finalWallMs = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], 0);
    const nowWallMs = Date.now() + fusoH * 3600 * 1000;
    return finalWallMs < nowWallMs;
  }, [fusoH]);

  const carregarReqRef = useRef(0);
  const carregar = useCallback(async () => {
    const reqId = ++carregarReqRef.current;
    setLoading(true);
    try {
      const url = modo === "vencidas"
        ? "/cliente/agendas/vencidas"
        : modo === "todos"
          ? "/cliente/agendas/todas"
          : "/cliente/agendas/hoje";
      const res = await clienteApi.get(url);
      // Ignora respostas obsoletas (ex.: fetch "hoje" disparado antes de setModo("vencidas"))
      if (reqId !== carregarReqRef.current) return;
      setAgendas(res.data || []);
      setCount((res.data || []).length);
    } catch {
      if (reqId === carregarReqRef.current) toast.error("Erro ao carregar agendas");
    } finally {
      if (reqId === carregarReqRef.current) setLoading(false);
    }
  }, [modo]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  useEffect(() => { if (open) { setModo(initialVencidas ? "vencidas" : "hoje"); setView(initialView); setEditing(null); } }, [open, initialView, initialVencidas]);
  useEffect(() => { if (open) carregar(); }, [open, carregar]);

  useEffect(() => {
    if (!open) return;
    const handleEsc = (e) => {
      if (e.key !== "Escape") return;
      if (view === "form") { setView("list"); setEditing(null); return; }
      onClose();
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [open, onClose, view]);

  const confirmar = async (agenda) => {
    const cpfDig = (agenda.cpf || "").replace(/\D/g, "");
    if (cpfDig.length !== 11) {
      // Agenda sem CPF → abre mini-modal para captura antes de confirmar
      setCpfPrompt({ agenda, cpfValor: "" });
      return;
    }
    try {
      // Lê a config de confirmação (cache 60s para não bater a cada clique)
      let confirmacaoManual = false;
      try {
        const r = await clienteApi.get("/cliente/configuracoes");
        confirmacaoManual = r.data?.confirmacao_agenda_manual === true;
      } catch {
        // Falha → assume automático
      }

      if (confirmacaoManual) {
        // Fecha modal e navega para o formulário de visitante já populado
        onClose && onClose();
        navigate(`/cliente/visitantes/novo?from_agenda=${agenda.id}`);
        return;
      }

      // Modo automático (default): efetiva direto
      await clienteApi.post(`/cliente/agendas/${agenda.id}/confirmar`);
      toast.success("Agenda confirmada como visitante!");
      await carregar();
      onConfirmed && onConfirmed();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erro ao confirmar agenda");
    }
  };

  const salvarCpfAgenda = async () => {
    if (!cpfPrompt) return;
    const cpfDig = (cpfPrompt.cpfValor || "").replace(/\D/g, "");
    if (cpfDig.length !== 11) { toast.error("Informe os 11 dígitos do CPF"); return; }
    if (!validarCPF(cpfDig)) { toast.error("CPF inválido — verifique os dígitos"); return; }
    setSavingCpfPrompt(true);
    try {
      const resp = await clienteApi.put(`/cliente/agendas/${cpfPrompt.agenda.id}`, { cpf: cpfDig });
      const ag = resp?.data || {};
      const nomeFinal = ag.nome || cpfPrompt.agenda.nome;
      const nomeAnterior = cpfPrompt.agenda.nome;
      if (nomeFinal && nomeAnterior && nomeFinal.trim().toUpperCase() !== nomeAnterior.trim().toUpperCase()) {
        toast.success(`CPF gravado. Nome ajustado para "${nomeFinal}" (cadastro anterior)`);
      } else {
        toast.success("CPF gravado na agenda");
      }
      setCpfPrompt(null);
      await carregar();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erro ao gravar CPF");
    } finally {
      setSavingCpfPrompt(false);
    }
  };

  const excluir = async (a) => {
    if (!window.confirm(`Excluir o agendamento de "${a.nome}"?`)) return;
    try {
      await clienteApi.delete(`/cliente/agendas/${a.id}`);
      toast.success("Agenda excluída");
      await carregar();
      onConfirmed && onConfirmed();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erro ao excluir agenda");
    }
  };

  // Encerra uma agenda vencida (status → encerrada; sai das listas de pendentes).
  const encerrar = async (a) => {
    if (!window.confirm(`Encerrar a agenda vencida de "${a.nome}"? Ela sairá da lista de pendentes.`)) return;
    try {
      await clienteApi.post(`/cliente/agendas/${a.id}/encerrar`);
      toast.success("Agenda encerrada");
      await carregar();
      onConfirmed && onConfirmed();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erro ao encerrar agenda");
    }
  };

  // Reagenda: abre o formulário de edição mantendo os dados, mas zerando as
  // datas para que o início volte a ser "agora" (o operador confirma/ajusta).
  const reagendar = (a) => {
    setEditing({ ...a, permanencia_inicial: "", permanencia_final: "" });
    setReagendarMode(true);
    setView("form");
  };

  if (!open) return null;

  const filtradas = agendas.filter((a) => {
    if (!filtro.trim()) return true;
    const termo = filtro.toLowerCase().trim();
    const nome = (a.nome || "").toLowerCase();
    const cpf = (a.cpf || "").replace(/\D/g, "");
    const placa = (a.placa || "").toUpperCase();
    const cpfBusca = termo.replace(/\D/g, "");
    // Nome: cada palavra digitada precisa aparecer (AND), em qualquer ordem.
    const tokens = termo.split(/\s+/).filter(Boolean);
    const nomeMatch = tokens.length > 0 && tokens.every((t) => nome.includes(t));
    return (
      nomeMatch ||
      (cpfBusca && cpf.includes(cpfBusca)) ||
      placa.includes(termo.toUpperCase())
    );
  });

  const sortGetter = (x) => {
    switch (sortKey) {
      case "local": return x.local_acesso_nome || "";
      case "nome": return x.nome || "";
      case "placa": return x.placa || "";
      case "autorizador": return x.autorizador_nome || "";
      case "permanencia_inicial":
      default: return x.permanencia_inicial || "";
    }
  };
  const ordenadas = [...filtradas].sort((a, b) => {
    const cmp = String(sortGetter(a)).localeCompare(String(sortGetter(b)), "pt-BR", { numeric: true, sensitivity: "base" });
    return sortDir === "asc" ? cmp : -cmp;
  });

  const SortTh = ({ label, col, align = "left" }) => (
    <th className={`text-${align} px-3 py-2 text-brand-ice/70 font-heading font-bold`}>
      <button
        type="button"
        data-testid={`agendas-sort-${col}`}
        onClick={() => toggleSort(col)}
        className={`inline-flex items-center gap-1 hover:text-brand-ice transition-colors ${sortKey === col ? "text-brand-light" : ""}`}
      >
        {label}
        {sortKey === col ? (
          sortDir === "asc" ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />
        ) : (
          <ChevronUp className="w-3.5 h-3.5 opacity-20" />
        )}
      </button>
    </th>
  );

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm"
      onClick={() => {
        // No formulário (criar/editar agenda), clicar fora NÃO fecha o modal — evita perder o cadastro
        if (view === "form") return;
        onClose();
      }}
    >
      <div
        className="relative w-full max-w-5xl max-h-[88vh] overflow-hidden flex flex-col rounded-2xl bg-login-gradient border border-white/15 shadow-[0_0_60px_rgba(80,140,220,0.35),inset_0_1px_0_rgba(255,255,255,0.1)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Fundo radial branco central (mesmo padrão da tela de login do cliente) */}
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(255,255,255,0.10)_0%,_rgba(78,106,156,0.08)_30%,_transparent_70%)] rounded-2xl" />
        <div className="relative z-10 flex flex-col flex-1 overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <h3 className="text-lg font-semibold text-brand-ice flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            {modo === "vencidas" ? `Agendas Vencidas (${count})` : modo === "todos" ? `Agendas · Hoje + Futuras (${count})` : `Agendas de Hoje (${count})`}
          </h3>
          <div className="flex items-center gap-2">
            {view === "list" && (
              <>
                <div className="flex items-center rounded-lg bg-white/5 border border-brand-ice/20 overflow-hidden">
                  <button
                    type="button"
                    data-testid="agendas-modo-hoje"
                    onClick={() => setModo("hoje")}
                    className={`px-3 py-1.5 text-sm font-bold flex items-center gap-1 transition-colors ${modo === "hoje" ? "bg-brand-medium text-white" : "text-brand-ice/70 hover:text-brand-ice hover:bg-white/10"}`}
                  >
                    <Calendar className="w-4 h-4" /> Hoje
                  </button>
                  <button
                    type="button"
                    data-testid="agendas-modo-todos"
                    onClick={() => setModo("todos")}
                    title="Listar agendas de hoje e futuras em aberto"
                    className={`px-3 py-1.5 text-sm font-bold flex items-center gap-1 transition-colors ${modo === "todos" ? "bg-brand-medium text-white" : "text-brand-ice/70 hover:text-brand-ice hover:bg-white/10"}`}
                  >
                    <List className="w-4 h-4" /> Todos
                  </button>
                  <button
                    type="button"
                    data-testid="agendas-modo-vencidas"
                    onClick={() => setModo("vencidas")}
                    title="Listar agendas vencidas (horário final já passou)"
                    className={`px-3 py-1.5 text-sm font-bold flex items-center gap-1 transition-colors ${modo === "vencidas" ? "bg-red-500/80 text-white" : "text-brand-ice/70 hover:text-brand-ice hover:bg-white/10"}`}
                  >
                    <AlertTriangle className="w-4 h-4" /> Vencidas
                    {vencidasCount > 0 && (
                      <span data-testid="agendas-vencidas-badge-count" className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${modo === "vencidas" ? "bg-white/25 text-white" : "bg-red-500/30 text-red-100"}`}>{vencidasCount}</span>
                    )}
                  </button>
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-ice/50" />
                  <input
                    type="text"
                    placeholder="Nome, CPF ou Placa..."
                    value={filtro}
                    onChange={(e) => setFiltro(e.target.value)}
                    className="pl-9 pr-3 py-1.5 text-sm rounded-lg bg-white/10 border border-brand-ice/20 text-brand-ice placeholder:text-brand-ice/50 focus:border-brand-light focus:outline-none w-64"
                    data-testid="agendas-modal-filtro"
                  />
                  {filtro && (
                    <button type="button" onClick={() => setFiltro("")} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-white/10 text-brand-ice/50 hover:text-brand-ice">
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => { setEditing(null); setReagendarMode(false); setView("form"); }}
                  data-testid="agendas-modal-incluir"
                  className="flex items-center gap-1 px-3 py-1.5 text-sm font-bold rounded-lg bg-brand-medium hover:bg-brand-dark text-white transition-colors"
                >
                  <Plus className="w-4 h-4" /> Incluir
                </button>
              </>
            )}
            <button
              type="button"
              data-testid="agendas-modal-close"
              onClick={() => { setFiltro(""); onClose(); }}
              className="p-2 rounded-lg text-brand-ice/60 hover:text-brand-ice hover:bg-white/10 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {view === "form" ? (
          <AgendaForm
            initial={editing || (initialCpf ? { cpf: initialCpf } : null)}
            reagendando={reagendarMode}
            onCancel={() => { setView("list"); setEditing(null); setReagendarMode(false); }}
            onSaved={async () => { setView("list"); setEditing(null); setReagendarMode(false); await carregar(); onConfirmed && onConfirmed(); }}
          />
        ) : (
          <div className="p-4 flex-1 overflow-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-ice"></div>
              </div>
            ) : agendas.length === 0 ? (
              <div className="flex items-center justify-center py-8 text-brand-ice/50">{modo === "vencidas" ? "Nenhuma agenda vencida 🎉" : modo === "todos" ? "Nenhuma agenda de hoje ou futura em aberto" : "Nenhuma agenda para hoje"}</div>
            ) : filtradas.length === 0 ? (
              <div className="flex items-center justify-center py-8 text-brand-ice/50">Nenhuma agenda encontrada para "{filtro}"</div>
            ) : (
              <div className="overflow-auto">
                <table className="w-full text-sm">
                  <thead className="bg-white/5 backdrop-blur-sm sticky top-0">
                    <tr>
                      <SortTh label="Local" col="local" />
                      <SortTh label="Visitante" col="nome" />
                      <SortTh label="Placa" col="placa" />
                      <SortTh label="Data/Hora" col="permanencia_inicial" />
                      <SortTh label="Autorizador" col="autorizador" />
                      <th className="text-center px-3 py-2 text-brand-ice/70 font-heading font-bold">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ordenadas.map((a) => {
                      const vencida = isVencida(a);
                      return (
                      <tr key={a.id} className={`border-t border-brand-ice/10 hover:bg-white/5 ${vencida ? "bg-red-500/[0.07]" : ""}`} data-testid={`agenda-row-${a.id}`}>
                        <td className="px-3 py-2 text-brand-ice">
                          {a.local_acesso_nome}
                          {a.unidade && <span className="text-brand-ice/50 ml-1">({a.unidade})</span>}
                        </td>
                        <td className="px-3 py-2 text-brand-ice">
                          <div>{a.nome}</div>
                          <div className="text-xs text-brand-ice/50">{formatCPFDisplay(a.cpf)}</div>
                          {a.motivo && (
                            <div className="text-xs text-brand-ice/50" data-testid={`agenda-motivo-${a.id}`}>{a.motivo}</div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-amber-300 font-mono">
                          {a.placa ? (
                            <div>
                              <div className="flex items-center gap-1">
                                <Car className="w-3 h-3" />
                                <span className="font-bold">{a.placa}</span>
                              </div>
                              {(a.modelo_veiculo || a.cor_veiculo) && (
                                <div className="text-brand-ice/50 text-xs ml-4 mt-0.5">
                                  {[a.modelo_veiculo, a.cor_veiculo].filter(Boolean).join(" - ")}
                                </div>
                              )}
                            </div>
                          ) : <span className="text-brand-ice/30">—</span>}
                        </td>
                        <td className="px-3 py-2 text-brand-ice">
                          <div
                            data-testid={`agenda-datahora-${a.id}`}
                            title={`Inicia ${formatarDDMMHM(a.permanencia_inicial)} e termina ${formatarDDMMHM(a.permanencia_final)}`}
                          >
                            <div>{formatarDDMMHM(a.permanencia_inicial)}</div>
                            <div className="text-xs text-brand-ice/50">{formatarDDMMHM(a.permanencia_final)}</div>
                            {vencida && (
                              <span data-testid={`agenda-vencida-${a.id}`} className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-500/20 border border-red-400/50 text-red-200 text-[9px] font-heading font-bold tracking-wider uppercase whitespace-nowrap">
                                <AlertTriangle className="w-3 h-3" /> Vencida
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-brand-ice/70">{a.autorizador_nome}</td>
                        <td className="px-3 py-2 text-center">
                          <div className="flex items-center justify-center gap-1">
                            {vencida ? (
                              <>
                                <button
                                  type="button"
                                  data-testid={`agenda-reagendar-${a.id}`}
                                  onClick={() => reagendar(a)}
                                  title="Reagendar para um novo horário"
                                  className="px-2.5 py-1 bg-sky-500/90 hover:bg-sky-500 text-white text-xs font-bold rounded-lg transition-colors flex items-center gap-1"
                                >
                                  <CalendarClock className="w-3.5 h-3.5" /> Reagendar
                                </button>
                                <button
                                  type="button"
                                  data-testid={`agenda-encerrar-${a.id}`}
                                  onClick={() => encerrar(a)}
                                  title="Encerrar esta agenda vencida"
                                  className="px-2.5 py-1 bg-orange-500/90 hover:bg-orange-500 text-white text-xs font-bold rounded-lg transition-colors flex items-center gap-1"
                                >
                                  <Ban className="w-3.5 h-3.5" /> Encerrar
                                </button>
                              </>
                            ) : ((a.cpf || "").replace(/\D/g, "").length === 11) ? (
                              <button
                                type="button"
                                data-testid={`agenda-confirmar-${a.id}`}
                                onClick={() => confirmar(a)}
                                className="px-3 py-1 bg-green-500 hover:bg-green-600 text-white text-xs font-bold rounded-lg transition-colors"
                              >
                                Confirmar
                              </button>
                            ) : (
                              <button
                                type="button"
                                data-testid={`agenda-confirme-cpf-${a.id}`}
                                onClick={() => setCpfPrompt({ agenda: a, cpfValor: "" })}
                                className="px-3 py-1 bg-amber-500 hover:bg-amber-600 text-brand-dark text-xs font-bold rounded-lg transition-colors"
                                title="CPF ausente — clique para informar"
                              >
                                Confirme CPF
                              </button>
                            )}
                            {!vencida && (
                              <button
                                type="button"
                                data-testid={`agenda-editar-${a.id}`}
                                onClick={() => { setEditing(a); setReagendarMode(false); setView("form"); }}
                                className="p-1.5 rounded-lg text-brand-ice/60 hover:text-brand-light hover:bg-white/10 transition-colors"
                                title="Editar"
                              >
                                <Pencil className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              type="button"
                              data-testid={`agenda-excluir-${a.id}`}
                              onClick={() => excluir(a)}
                              className="p-1.5 rounded-lg text-brand-ice/60 hover:text-red-400 hover:bg-white/10 transition-colors"
                              title="Excluir"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end p-4 border-t border-white/10">
          <button
            type="button"
            onClick={() => { setFiltro(""); onClose(); }}
            className="px-4 py-2 text-sm font-heading font-bold text-brand-ice/70 hover:text-brand-ice transition-colors"
          >
            Fechar
          </button>
        </div>
        </div>
      </div>

      {/* Mini-modal de captura de CPF para agendas sem CPF */}
      {cpfPrompt && (
        <div
          className="fixed inset-0 bg-black/60 z-[110] flex items-center justify-center p-4"
          onClick={(e) => { e.stopPropagation(); setCpfPrompt(null); }}
        >
          <div
            className="bg-brand-dark border border-amber-400/40 rounded-xl shadow-2xl w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-3 border-b border-amber-400/30">
              <h3 className="font-heading font-bold text-amber-200">Informe o CPF do visitante</h3>
              <p className="text-xs text-brand-ice/60 mt-0.5">Visitante: <b>{cpfPrompt.agenda.nome}</b></p>
            </div>
            <div className="p-5 space-y-3">
              <Label className="text-[10px] uppercase tracking-wider font-heading font-bold text-brand-ice/70">CPF *</Label>
              <Input
                autoFocus
                data-testid="agenda-cpf-prompt-input"
                value={cpfPrompt.cpfValor}
                onChange={(e) => setCpfPrompt({ ...cpfPrompt, cpfValor: formatCPFInput(e.target.value) })}
                placeholder="000.000.000-00"
                className="bg-white/10 border-brand-ice/20 text-brand-ice placeholder:text-brand-ice/40 focus:border-brand-light focus:ring-brand-light/20"
                onKeyDown={(e) => { if (e.key === "Enter") salvarCpfAgenda(); }}
              />
              <p className="text-[11px] text-brand-ice/50">
                Ao gravar, o CPF será salvo no registro da agenda e o botão &quot;Confirmar&quot; ficará disponível.
              </p>
            </div>
            <div className="flex justify-end gap-3 px-5 py-3 border-t border-amber-400/30">
              <button
                type="button"
                onClick={() => setCpfPrompt(null)}
                className="px-4 py-2 text-sm text-brand-ice/70 hover:text-brand-ice transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                data-testid="agenda-cpf-prompt-salvar"
                onClick={salvarCpfAgenda}
                disabled={savingCpfPrompt}
                className="px-4 py-2 text-sm font-bold bg-amber-500 hover:bg-amber-600 text-brand-dark rounded-lg transition-colors disabled:opacity-60"
              >
                {savingCpfPrompt ? "Gravando..." : "Gravar CPF"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
